import { z } from 'zod';
import { cursorQuerySchema, urlHttpSchema } from './core.js';
/* ------------------------------------------------------------------ */
/* Hằng số                                                             */
/* ------------------------------------------------------------------ */
export const MUSIC_SOURCE_KINDS = ['own', 'partner', 'network_library'];
export const musicSourceKindSchema = z.enum(MUSIC_SOURCE_KINDS);
export const MUSIC_SOURCE_KIND_LABEL = {
    own: 'Miss Records',
    partner: 'Đối tác nhạc',
    network_library: 'Kho nhạc Network',
};
export const CONTENT_ID_STATUSES = ['registered', 'pending', 'not_registered'];
export const contentIdStatusSchema = z.enum(CONTENT_ID_STATUSES);
export const CONTENT_ID_LABEL = {
    registered: 'Đã đăng ký',
    pending: 'Đang chờ',
    not_registered: 'Chưa đăng ký',
};
export const TRACK_STATUSES = ['active', 'blocked', 'expired'];
export const trackStatusSchema = z.enum(TRACK_STATUSES);
export const WHITELIST_STATUSES = ['whitelisted', 'requested', 'missing'];
export const whitelistStatusSchema = z.enum(WHITELIST_STATUSES);
/** Giấy phép còn dưới số ngày này thì coi là sắp hết hạn. */
export const SAP_HET_HAN_NGAY = 30;
/** Lý do một bài nhạc không dùng được cho kênh. */
export const TRACK_BLOCK_REASONS = [
    'license_expired',
    'license_expiring',
    'not_whitelisted',
    'network_library_mismatch',
    'track_blocked',
];
export const TRACK_BLOCK_REASON_LABEL = {
    license_expired: 'Giấy phép đã hết hạn',
    license_expiring: 'Giấy phép sắp hết hạn (dưới 30 ngày)',
    not_whitelisted: 'Kênh chưa được whitelist cho bài đã đăng ký Content ID',
    network_library_mismatch: 'Nhạc thuộc kho Network mà kênh không tham gia',
    track_blocked: 'Bài nhạc đang bị khoá',
};
/* ------------------------------------------------------------------ */
/* Nguồn nhạc và bài nhạc                                              */
/* ------------------------------------------------------------------ */
export const musicSourceSchema = z.object({
    id: z.string(),
    name: z.string(),
    kind: musicSourceKindSchema,
    partnerRef: z.string().nullable(),
    licenseTerms: z.string().nullable(),
    creditRequired: z.boolean(),
    creditTemplate: z.string().nullable(),
    licenseEndDate: z.iso.datetime().nullable(),
    autoWhitelistForPartnerId: z.string().nullable(),
    trackCount: z.number().int().nonnegative(),
});
export const trackSchema = z.object({
    id: z.string(),
    sourceId: z.string(),
    sourceName: z.string(),
    sourceKind: musicSourceKindSchema,
    title: z.string(),
    artist: z.string(),
    isrc: z.string().nullable(),
    genre: z.string().nullable(),
    moods: z.array(z.string()),
    bpm: z.number().int().nullable(),
    durationSec: z.number().int().nonnegative(),
    previewUrl: z.string().nullable(),
    contentIdStatus: contentIdStatusSchema,
    licenseEndDate: z.iso.datetime().nullable(),
    status: trackStatusSchema,
    /// Số kênh đã whitelist trên tổng số kênh đang hoạt động.
    whitelistedCount: z.number().int().nonnegative(),
    channelCount: z.number().int().nonnegative(),
    usageCount: z.number().int().nonnegative(),
});
export const trackWhitelistSchema = z.object({
    channelId: z.string(),
    channelName: z.string(),
    status: whitelistStatusSchema,
    requestedAt: z.iso.datetime().nullable(),
    confirmedAt: z.iso.datetime().nullable(),
});
export const trackUsageSchema = z.object({
    id: z.string(),
    trackId: z.string(),
    trackTitle: z.string(),
    channelId: z.string(),
    channelName: z.string(),
    videoRef: z.string(),
    usedAt: z.iso.datetime(),
});
/* ------------------------------------------------------------------ */
/* Kiểm tra và gợi ý                                                   */
/* ------------------------------------------------------------------ */
/** Kết quả `checkTrackForChannel(trackId, channelId)`. */
export const trackCheckResultSchema = z.object({
    ok: z.boolean(),
    reasons: z.array(z.object({
        code: z.enum(TRACK_BLOCK_REASONS),
        message: z.string(),
        /// `true` khi chỉ là cảnh báo, vẫn dùng được.
        warning: z.boolean(),
    })),
});
export const suggestTracksInputSchema = z.object({
    channelId: z.string().min(1),
    moods: z.array(z.string()).optional(),
    bpmRange: z.tuple([z.number().int(), z.number().int()]).optional(),
    minDuration: z.number().int().nonnegative().optional(),
    limit: z.number().int().min(1).max(50).default(10),
});
export const recordUsageInputSchema = z.object({
    trackId: z.string().min(1),
    channelId: z.string().min(1),
    /// Id ProductionItem hoặc youtubeVideoId.
    videoRef: z.string().min(1),
});
/** Bài đã đăng ký Content ID mà kênh chưa được whitelist. */
export const whitelistGapSchema = z.object({
    trackId: z.string(),
    title: z.string(),
    artist: z.string(),
    sourceId: z.string(),
    sourceName: z.string(),
    sourceKind: musicSourceKindSchema,
    channelId: z.string(),
    channelName: z.string(),
    status: whitelistStatusSchema,
    requestedAt: z.iso.datetime().nullable(),
    /// `true` khi kênh đang dùng bài này trong video đã đăng.
    dangSuDung: z.boolean(),
});
/** Nhạc gắn với một video, dùng cho cột "Nhạc sử dụng" của module analytics. */
export const trackRefSchema = z.object({
    trackId: z.string(),
    title: z.string(),
    artist: z.string(),
    sourceKind: musicSourceKindSchema,
});
/** Một dòng của `listExpiringTracks(days)`. */
export const expiringTrackSchema = z.object({
    trackId: z.string(),
    title: z.string(),
    artist: z.string(),
    sourceName: z.string(),
    licenseEndDate: z.iso.datetime(),
    daysLeft: z.number().int(),
    /// Các kênh đang dùng bài này.
    channelIds: z.array(z.string()),
});
/* ------------------------------------------------------------------ */
/* Input và truy vấn                                                   */
/* ------------------------------------------------------------------ */
export const listTracksQuerySchema = cursorQuerySchema.extend({
    q: z.string().trim().optional(),
    sourceKind: musicSourceKindSchema.optional(),
    sourceId: z.string().optional(),
    mood: z.string().optional(),
    bpmMin: z.coerce.number().int().optional(),
    bpmMax: z.coerce.number().int().optional(),
    status: trackStatusSchema.optional(),
    contentIdStatus: contentIdStatusSchema.optional(),
});
export const createTrackInputSchema = z.object({
    sourceId: z.string().min(1),
    title: z.string().trim().min(1).max(200),
    artist: z.string().trim().min(1).max(200),
    isrc: z.string().trim().max(20).nullish(),
    genre: z.string().trim().max(60).nullish(),
    moods: z.array(z.string().trim().min(1).max(30)).max(10).default([]),
    bpm: z.number().int().min(20).max(300).nullish(),
    durationSec: z.number().int().nonnegative().default(0),
    /** Chỉ `http(s)` — xem `urlHttpSchema` (rà soát 16/10/2026, mục T1). */
    previewUrl: urlHttpSchema.nullish(),
    contentIdStatus: contentIdStatusSchema.default('not_registered'),
    licenseEndDate: z.iso.datetime().nullish(),
    status: trackStatusSchema.default('active'),
});
export const updateTrackInputSchema = createTrackInputSchema.partial();
export const requestWhitelistInputSchema = z.object({
    trackId: z.string().nullish(),
    sourceId: z.string().nullish(),
    channelIds: z.array(z.string().min(1)).min(1).max(50),
});
export const setWhitelistStatusInputSchema = z.object({
    status: whitelistStatusSchema,
});
/** 4 thẻ chỉ số của màn hình Kho nhạc. */
export const musicSummarySchema = z.object({
    totalTracks: z.number().int().nonnegative(),
    ownTracks: z.number().int().nonnegative(),
    partnerTracks: z.number().int().nonnegative(),
    registeredContentId: z.number().int().nonnegative(),
    expiringSoon: z.number().int().nonnegative(),
    missingWhitelist: z.number().int().nonnegative(),
});
/* ------------------------------------------------------------------ */
/* Bổ sung ở module 04 (chỉ thêm, không sửa phần đã chốt 01b)           */
/* ------------------------------------------------------------------ */
/**
 * Số liệu phụ cho ghi chú dưới 4 thẻ chỉ số của màn hình Kho nhạc.
 * `MusicSummary` đã chốt ở 01b nên phần này tách thành endpoint riêng
 * `GET /api/v1/music/thong-ke`.
 */
export const musicExtraStatsSchema = z.object({
    /// Số lượt dùng nhạc trong tháng hiện tại (giờ Việt Nam).
    usageThisMonth: z.number().int().nonnegative(),
    /// Tỉ lệ phần trăm lượt dùng là nhạc Miss Records.
    ownUsagePercent: z.number().int().min(0).max(100),
    partnerUsagePercent: z.number().int().min(0).max(100),
    /// Số nguồn nhạc hợp tác còn giấy phép hiệu lực.
    partnerSourceCount: z.number().int().nonnegative(),
    /// Số video đang dùng bài có giấy phép sắp hết hạn.
    expiringVideoCount: z.number().int().nonnegative(),
});
/** Ánh xạ cột CSV → trường của `Track`. Giá trị là tên cột trong file. */
export const trackCsvMappingSchema = z.object({
    title: z.string().trim().min(1).max(100).optional(),
    artist: z.string().trim().min(1).max(100).optional(),
    isrc: z.string().trim().min(1).max(100).optional(),
    genre: z.string().trim().min(1).max(100).optional(),
    moods: z.string().trim().min(1).max(100).optional(),
    bpm: z.string().trim().min(1).max(100).optional(),
    durationSec: z.string().trim().min(1).max(100).optional(),
    licenseEndDate: z.string().trim().min(1).max(100).optional(),
});
/**
 * Nhập danh mục bài nhạc từ CSV. Trình duyệt đọc file bằng `FileReader` rồi
 * gửi nguyên nội dung; API không nhận multipart.
 */
export const importTracksInputSchema = z.object({
    sourceId: z.string().min(1),
    csv: z.string().min(1).max(2_000_000),
    mapping: trackCsvMappingSchema.default({}),
    /// Bài đã có (khớp ISRC hoặc tên + nguồn) thì cập nhật thay vì bỏ qua.
    capNhatNeuTrung: z.boolean().default(true),
});
export const importTracksResultSchema = z.object({
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    /// Dòng không đọc được, kèm số thứ tự dòng trong file.
    errors: z.array(z.object({ line: z.number().int(), message: z.string() })),
});
//# sourceMappingURL=music.js.map